/**
 * Overlay registry — color ramps, opacities, and legend data
 * are transcribed verbatim from the captured MapLibre style JSON at:
 *   docs/skysight-overlay-styles/pfd.json
 *   docs/skysight-overlay-styles/thermal-height.json
 *   docs/skysight-overlay-styles/thermal-strength.json
 *   docs/skysight-overlay-styles/cu-cloudbase.json
 *
 * DO NOT edit color values here without diffing against the source files.
 */

export const OVERLAYS = [
  // ─── PFD (Potential Flight Distance) ────────────────────────────────────────
  // Source: pfd.json › overlayLayers[0] (pfdtot:202606081800), fill layer
  // Source pfdtot:202606081800 confirms this is a daily total fixed at 1800 UTC
  {
    id: 'pfd',
    group: 'Thermals',
    label: 'Potential Flight Distance',
    description:
      'Distance a paraglider/hang glider might be expected to fly, taking off at first thermals and landing at last, assuming MacCready theory in high conditions and slower in weak.',
    timeVarying: false,
    fixedTime: '1800',
    params: [
      {
        name: 'pfdtot',
        opacity: 0.5,
        colorRamp: [
          'rgb(0, 0, 143)',
          'rgb(0, 68, 255)',
          'rgb(0, 248, 255)',
          'rgb(5, 203, 92)',
          'rgb(104, 208, 9)',
          'rgb(255, 247, 0)',
          'rgb(255, 180, 0)',
          'rgb(255, 75, 0)',
          'rgb(213, 0, 35)',
          'rgb(94, 0, 131)',
        ],
      },
    ],
    legend: {
      unit: 'km',
      stops: [
        { label: '>779', color: 'rgb(151, 96, 173)' },
        { label: '697',  color: 'rgb(221, 96, 116)' },
        { label: '615',  color: 'rgb(246, 140, 96)' },
        { label: '533',  color: 'rgb(246, 202, 96)' },
        { label: '451',  color: 'rgb(246, 241, 96)' },
        { label: '369',  color: 'rgb(157, 218, 101)' },
        { label: '287',  color: 'rgb(99, 215, 150)' },
        { label: '205',  color: 'rgb(96, 242, 246)' },
        { label: '123',  color: 'rgb(96, 136, 246)' },
        { label: '41',   color: 'rgb(96, 96, 180)' },
      ],
    },
  },

  // ─── Thermal Height ──────────────────────────────────────────────────────────
  // Source: thermal-height.json › overlayLayers[0] (hwcrit:202606081400), fill layer
  // NB: thermal-height.json contains no legend array; stops are derived from the
  // 22 unique ramp entries mapped to 200 m intervals (matching the ramp scale).
  {
    id: 'thermal-height',
    group: 'Thermals',
    label: 'Height of Thermals',
    description: 'Height above ground level at which thermals are expected to reach.',
    timeVarying: true,
    params: [
      {
        name: 'hwcrit',
        opacity: 0.5,
        colorRamp: [
          'rgb(0, 0, 143)',
          'rgb(0, 0, 220)',
          'rgb(0, 43, 255)',
          'rgb(0, 119, 255)',
          'rgb(0, 197, 255)',
          'rgb(0, 249, 237)',
          'rgb(2, 227, 165)',
          'rgb(5, 203, 92)',
          'rgb(8, 180, 20)',
          'rgb(78, 200, 11)',
          'rgb(153, 224, 6)',
          'rgb(228, 247, 2)',
          'rgb(255, 237, 0)',
          'rgb(255, 208, 0)',
          'rgb(255, 180, 0)',
          'rgb(255, 142, 0)',
          'rgb(255, 91, 0)',
          'rgb(255, 42, 0)',
          'rgb(246, 0, 7)',
          'rgb(196, 0, 48)',
          'rgb(145, 0, 89)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
        ],
      },
    ],
    legend: {
      unit: 'm',
      // Captured live from skysight.io #/thermal-height legend (DOM swatch colors),
      // not derived from the fill ramp — these are the terrain-blended display
      // colors Skysight actually shows. Order high→low to match Skysight + the
      // other overlays. Source: docs/skysight-overlay-styles/thermal-height.legend.json
      stops: [
        { label: '>4400', color: 'rgb(151, 96, 173)' },
        { label: '4200',  color: 'rgb(181, 96, 148)' },
        { label: '4000',  color: 'rgb(211, 96, 124)' },
        { label: '3800',  color: 'rgb(241, 96, 100)' },
        { label: '3600',  color: 'rgb(246, 121, 96)' },
        { label: '3400',  color: 'rgb(246, 150, 96)' },
        { label: '3200',  color: 'rgb(246, 179, 96)' },
        { label: '3000',  color: 'rgb(246, 202, 96)' },
        { label: '2800',  color: 'rgb(246, 222, 96)' },
        { label: '2600',  color: 'rgb(246, 235, 96)' },
        { label: '2400',  color: 'rgb(230, 241, 97)' },
        { label: '2200',  color: 'rgb(186, 228, 100)' },
        { label: '2000',  color: 'rgb(142, 214, 102)' },
        { label: '1800',  color: 'rgb(101, 202, 107)' },
        { label: '1600',  color: 'rgb(99, 215, 150)' },
        { label: '1400',  color: 'rgb(98, 229, 193)' },
        { label: '1200',  color: 'rgb(96, 243, 236)' },
        { label: '1000',  color: 'rgb(96, 212, 246)' },
        { label: '800',   color: 'rgb(96, 166, 246)' },
        { label: '600',   color: 'rgb(96, 121, 246)' },
        { label: '400',   color: 'rgb(96, 96, 225)' },
        { label: '200',   color: 'rgb(96, 96, 180)' },
      ],
    },
  },

  // ─── Thermal Strength ────────────────────────────────────────────────────────
  // Source: thermal-strength.json › overlayLayers[0] (wstar_bsratio:202606081400), fill layer
  // Stipple layer (bsratio) is DEFERRED — base color ramp only.
  {
    id: 'thermal-strength',
    group: 'Thermals',
    label: 'Thermal Strength',
    description: 'Strength of thermals.',
    timeVarying: true,
    params: [
      {
        name: 'wstar_bsratio',
        opacity: 0.5,
        colorRamp: [
          'rgb(0, 0, 143)',
          'rgb(0, 119, 255)',
          'rgb(2, 227, 165)',
          'rgb(78, 200, 11)',
          'rgb(255, 237, 0)',
          'rgb(255, 142, 0)',
          'rgb(246, 0, 7)',
          'rgb(94, 0, 131)',
        ],
      },
    ],
    legend: {
      unit: 'm/s',
      stops: [
        { label: '>4',  color: 'rgb(151, 96, 173)' },
        { label: '3.5', color: 'rgb(241, 96, 100)' },
        { label: '3',   color: 'rgb(246, 179, 96)' },
        { label: '2.5', color: 'rgb(246, 235, 96)' },
        { label: '2',   color: 'rgb(142, 214, 102)' },
        { label: '1.5', color: 'rgb(98, 229, 193)' },
        { label: '1',   color: 'rgb(96, 166, 246)' },
        { label: '0.5', color: 'rgb(96, 96, 180)' },
      ],
    },
  },

  // ─── Cu Cloudbase ────────────────────────────────────────────────────────────
  // Source: cu-cloudbase.json
  // Two params, grey low-cloud layer FIRST (rendered under the colored cloudbase):
  //   1. zsfclclmask_low  — constant grey (#A9A9A9), uses paintOverride (not a ramp)
  //   2. zsfclclmask      — standard ramp fill layer
  {
    id: 'cu-cloudbase',
    group: 'Clouds',
    label: 'Cu Cloudbase',
    description:
      'In areas where cumulus is expected to form, the height of that cumulus above sea level. Grey indicates possible cloud.',
    timeVarying: true,
    params: [
      {
        // Grey "possible-cloud" layer rendered below the colored cloudbase.
        // fill-color is a constant "#A9A9A9" (not a ramp expression), so
        // paintOverride is used and colorRamp/opacity are omitted.
        name: 'zsfclclmask_low',
        paintOverride: {
          'fill-color': '#A9A9A9',
          'fill-opacity': 0.5,
          'fill-opacity-transition': { duration: 500, delay: 0 },
        },
      },
      {
        // Colored cloudbase layer.
        // Source: zsfclclmask:202606081400 fill layer in cu-cloudbase.json
        name: 'zsfclclmask',
        opacity: 0.5,
        colorRamp: [
          'rgb(0, 0, 143)',
          'rgb(0, 0, 216)',
          'rgb(0, 36, 255)',
          'rgb(0, 109, 255)',
          'rgb(0, 183, 255)',
          'rgb(0, 255, 254)',
          'rgb(2, 233, 184)',
          'rgb(4, 210, 115)',
          'rgb(7, 189, 46)',
          'rgb(48, 191, 13)',
          'rgb(120, 213, 8)',
          'rgb(191, 236, 4)',
          'rgb(255, 252, 0)',
          'rgb(255, 225, 0)',
          'rgb(255, 198, 0)',
          'rgb(255, 171, 0)',
          'rgb(255, 128, 0)',
          'rgb(255, 80, 0)',
          'rgb(255, 32, 0)',
          'rgb(240, 0, 13)',
          'rgb(191, 0, 52)',
          'rgb(142, 0, 91)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
          'rgb(94, 0, 131)',
        ],
      },
    ],
    legend: {
      unit: 'm',
      stops: [
        { label: '>4400', color: 'rgb(151, 96, 173)' },
        { label: '4200',  color: 'rgb(180, 96, 149)' },
        { label: '4000',  color: 'rgb(208, 96, 126)' },
        { label: '3800',  color: 'rgb(237, 96, 103)' },
        { label: '3600',  color: 'rgb(246, 115, 96)' },
        { label: '3400',  color: 'rgb(246, 143, 96)' },
        { label: '3200',  color: 'rgb(246, 171, 96)' },
        { label: '3000',  color: 'rgb(246, 197, 96)' },
        { label: '2800',  color: 'rgb(246, 213, 96)' },
        { label: '2600',  color: 'rgb(246, 228, 96)' },
        { label: '2400',  color: 'rgb(246, 244, 96)' },
        { label: '2200',  color: 'rgb(208, 235, 98)' },
        { label: '2000',  color: 'rgb(166, 221, 101)' },
        { label: '1800',  color: 'rgb(124, 208, 104)' },
        { label: '1600',  color: 'rgb(100, 207, 123)' },
        { label: '1400',  color: 'rgb(99, 220, 164)' },
        { label: '1200',  color: 'rgb(97, 233, 204)' },
        { label: '1000',  color: 'rgb(96, 246, 245)' },
        { label: '800',   color: 'rgb(96, 204, 246)' },
        { label: '600',   color: 'rgb(96, 160, 246)' },
        { label: '400',   color: 'rgb(96, 117, 246)' },
        { label: '200',   color: 'rgb(96, 96, 223)' },
        { label: '0',     color: 'rgb(96, 96, 180)' },
      ],
    },
  },

  // ─── Cu Coverage (oktas) ─────────────────────────────────────────────────────
  // Source: captured LIVE from skysight.io #/oktas-cu map style (2026-06-11) —
  // fill layer `oktas_cu:{time}` ramp + DOM legend. 9 idx levels, % of sky.
  {
    id: 'oktas-cu',
    group: 'Clouds',
    label: 'Cu Coverage',
    description: 'Percentage of the sky covered by cumulus clouds.',
    timeVarying: true,
    params: [
      {
        name: 'oktas_cu',
        opacity: 0.5,
        colorRamp: [
          'rgb(0, 0, 143)',
          'rgb(0, 91, 255)',
          'rgb(0, 244, 219)',
          'rgb(8, 183, 29)',
          'rgb(191, 236, 4)',
          'rgb(255, 205, 0)',
          'rgb(255, 104, 0)',
          'rgb(227, 0, 23)',
          'rgb(94, 0, 131)',
        ],
      },
    ],
    legend: {
      unit: '%',
      stops: [
        { label: '>100', color: 'rgb(151, 96, 173)' },
        { label: '87.5', color: 'rgb(230, 96, 109)' },
        { label: '75',   color: 'rgb(246, 157, 96)' },
        { label: '62.5', color: 'rgb(246, 217, 96)' },
        { label: '50',   color: 'rgb(208, 235, 98)' },
        { label: '37.5', color: 'rgb(100, 204, 113)' },
        { label: '25',   color: 'rgb(97, 239, 225)' },
        { label: '12.5', color: 'rgb(96, 149, 246)' },
        { label: '0',    color: 'rgb(96, 96, 180)' },
      ],
    },
  },

  // ─── CAPE / Storms ───────────────────────────────────────────────────────────
  // Source: captured LIVE from skysight.io #/cape map style (2026-06-11) — fill
  // layer `cape:{time}` ramp + DOM legend. 11 idx levels, J/kg, 300-step bins.
  // Skysight also draws a `{time}_extra` symbol layer (storm icons, sprite
  // `cc7`) on top — not rendered here yet (follow-up).
  {
    id: 'cape',
    group: 'Clouds',
    label: 'CAPE / Storms',
    description:
      'Convective Available Potential Energy. Higher values indicate greater potential instability and thunderstorm risk.',
    timeVarying: true,
    params: [
      {
        name: 'cape',
        opacity: 0.5,
        colorRamp: [
          'rgb(0, 0, 143)',
          'rgb(0, 50, 255)',
          'rgb(0, 212, 255)',
          'rgb(4, 220, 143)',
          'rgb(34, 187, 13)',
          'rgb(191, 236, 4)',
          'rgb(255, 220, 0)',
          'rgb(255, 156, 0)',
          'rgb(255, 52, 0)',
          'rgb(200, 0, 45)',
          'rgb(94, 0, 131)',
        ],
      },
    ],
    legend: {
      unit: 'J/kg',
      stops: [
        { label: '>3100', color: 'rgb(151, 96, 173)' },
        { label: '2800',  color: 'rgb(214, 96, 122)' },
        { label: '2500',  color: 'rgb(246, 126, 96)' },
        { label: '2200',  color: 'rgb(246, 188, 96)' },
        { label: '1900',  color: 'rgb(246, 225, 96)' },
        { label: '1600',  color: 'rgb(208, 235, 98)' },
        { label: '1300',  color: 'rgb(116, 206, 104)' },
        { label: '1000',  color: 'rgb(98, 225, 180)' },
        { label: '700',   color: 'rgb(96, 221, 246)' },
        { label: '400',   color: 'rgb(96, 125, 246)' },
        { label: '100',   color: 'rgb(96, 96, 180)' },
      ],
    },
  },

  // ─── Cloud Cover (low/mid/high RGB mix) ──────────────────────────────────────
  // Source: captured LIVE from skysight.io #/cloud-cover map style (2026-06-11).
  // ONE edge tile (CFRACL) embeds three source-layers: `{time}` = low cloud,
  // `CFRACM` = mid, `CFRACH` = high. Each draws a single hue whose OPACITY
  // encodes the fraction: (idx + 1) / 9, idx 0..5. Low blue, mid green, high red.
  {
    id: 'cloud-cover',
    group: 'Clouds',
    label: 'Cloud Cover',
    description:
      'Cloud cover forecast, divided into Low (0-4000m), Medium (4000-8000m) and High (>8000m) cloud.',
    timeVarying: true,
    params: [
      {
        name: 'CFRACL',
        paintOverride: {
          'fill-color': '#1479f5',
          'fill-opacity': ['/', ['+', ['get', 'idx'], 1], 9],
        },
      },
      {
        name: 'CFRACL',
        sourceLayer: 'CFRACM',
        paintOverride: {
          'fill-color': '#10e309',
          'fill-opacity': ['/', ['+', ['get', 'idx'], 1], 9],
        },
      },
      {
        name: 'CFRACL',
        sourceLayer: 'CFRACH',
        paintOverride: {
          'fill-color': '#f51f14',
          'fill-opacity': ['/', ['+', ['get', 'idx'], 1], 9],
        },
      },
    ],
    legend: {
      unit: '',
      stops: [
        { label: 'High cloud (>8000 m)', color: 'rgb(240, 102, 108)' },
        { label: 'Mid cloud (4000-8000 m)', color: 'rgb(105, 230, 101)' },
        { label: 'Low cloud (<4000 m)', color: 'rgb(108, 167, 240)' },
      ],
    },
  },
];

export const getOverlay = (id) => OVERLAYS.find((o) => o.id === id) || null;

export const OVERLAY_GROUPS = OVERLAYS.reduce((acc, o) => {
  (acc[o.group] = acc[o.group] || []).push(o.id);
  return acc;
}, {});
